import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link2, Star, UserRound } from "lucide-react";

const StarPicker = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} aria-label={`${n} star`} data-testid={`${testid}-${n}`}>
        <Star size={20} className={n <= value ? "fill-current text-secondary" : "text-muted-foreground/40"} />
      </button>
    ))}
  </div>
);

/**
 * The member's own 1-on-1 evenings: the guru's session link for what is coming up,
 * and a star rating + comment for sessions that are already done.
 */
export const MySessionsCard = () => {
  const [bookings, setBookings] = useState([]);
  const [reviewable, setReviewable] = useState([]);
  const [mine, setMine] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    const [b, r, m] = await Promise.all([
      api.get("/yoga/one-on-one/mine").catch(() => ({ data: [] })),
      api.get("/gurus/reviewable").catch(() => ({ data: [] })),
      api.get("/gurus/my-reviews").catch(() => ({ data: [] })),
    ]);
    setBookings(b.data);
    setReviewable(r.data);
    setMine(m.data);
  };
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.status !== "cancelled" && b.date >= today);

  const submit = async (session) => {
    const draft = drafts[session.booking_id] || {};
    if (!draft.rating) return toast.error("Pick a star rating first");
    setBusy(session.booking_id);
    try {
      await api.post(`/gurus/${session.guru_id}/reviews`, {
        booking_id: session.booking_id,
        rating: draft.rating,
        comment: draft.comment || "",
      });
      toast.success(`Thank you — your review of ${session.guru_name} is saved`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!bookings.length && !mine.length) return null;

  return (
    <Card className="border-border" data-testid="my-sessions-card">
      <CardContent className="p-6 lg:p-8 space-y-8">
        <div>
          <h3 className="font-display text-2xl mb-1">My 1-on-1 sessions</h3>
          <p className="text-sm text-muted-foreground">Your guru&apos;s meeting link, and a place to rate the sessions you have finished.</p>
        </div>

        {upcoming.length > 0 && (
          <div className="space-y-3" data-testid="upcoming-sessions">
            <div className="overline">Coming up</div>
            {upcoming.map((b) => (
              <div key={b.id} className="border border-border rounded-xl p-4" data-testid={`upcoming-session-${b.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <UserRound size={15} className="text-secondary" />
                  <span className="font-medium">{b.guru_name || "Guru to be assigned"}</span>
                  {b.guru_speciality && <Badge variant="outline" className="text-[10px]">{b.guru_speciality}</Badge>}
                  <span className="text-xs text-muted-foreground ml-auto">{b.date} · {b.slot}</span>
                </div>
                {b.session_link ? (
                  <a
                    href={b.session_link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline mt-3"
                    data-testid={`session-link-${b.id}`}
                  >
                    <Link2 size={13} /> Join your session
                  </a>
                ) : (
                  <p className="text-xs text-muted-foreground mt-3" data-testid={`session-link-pending-${b.id}`}>
                    Your guru&apos;s meeting link will appear here shortly.
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {reviewable.length > 0 && (
          <div className="space-y-4" data-testid="reviewable-sessions">
            <div className="overline">Rate your guru</div>
            {reviewable.map((s) => (
              <div key={s.booking_id} className="border border-border rounded-xl p-4" data-testid={`review-form-${s.booking_id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="font-medium">{s.guru_name}</span>
                  <span className="text-xs text-muted-foreground">session on {s.date}</span>
                </div>
                <StarPicker
                  value={drafts[s.booking_id]?.rating || 0}
                  onChange={(rating) => setDrafts((d) => ({ ...d, [s.booking_id]: { ...d[s.booking_id], rating } }))}
                  testid={`review-stars-${s.booking_id}`}
                />
                <Textarea
                  rows={2}
                  placeholder="Anything you want to say about the session (optional)"
                  className="mt-3 bg-transparent"
                  value={drafts[s.booking_id]?.comment || ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [s.booking_id]: { ...d[s.booking_id], comment: e.target.value } }))}
                  data-testid={`review-comment-${s.booking_id}`}
                />
                <Button
                  onClick={() => submit(s)}
                  disabled={busy === s.booking_id}
                  className="btn-pill bg-primary text-primary-foreground h-10 mt-3"
                  data-testid={`review-submit-${s.booking_id}`}
                >
                  {busy === s.booking_id ? "Saving…" : "Submit review"}
                </Button>
              </div>
            ))}
          </div>
        )}

        {mine.length > 0 && (
          <div className="space-y-2" data-testid="my-reviews">
            <div className="overline">Reviews you have left</div>
            {mine.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 text-sm border-b border-border pb-2" data-testid={`my-review-${r.id}`}>
                <span className="font-medium">{r.guru_name}</span>
                <span className="inline-flex items-center gap-0.5 text-secondary">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={12} className={n <= r.rating ? "fill-current" : "opacity-30"} />)}
                </span>
                {r.comment && <span className="text-xs text-muted-foreground">“{r.comment}”</span>}
                <span className="text-xs text-muted-foreground ml-auto">{r.session_date}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MySessionsCard;
